/**
 * Инлайн-редактируемая ячейка для таблицы остатков.
 *
 * Два режима:
 *  - kind="number"   — input type=text inputMode=decimal (для бронь/критич/цены)
 *  - kind="text"     — обычный text input (для примечаний)
 *  - kind="select"   — выпадайка из списка опций (для ед.изм.)
 *
 * Клик/Tab/Enter — переход в режим редактирования.
 * Enter — сохранить, Esc — отмена, blur — сохранить.
 */
import { useEffect, useRef, useState } from "react";


interface BaseProps {
  value: string | number | null;
  saving?: boolean;
  className?: string;
  align?: "left" | "right" | "center";
  format?: (v: string | number | null) => string;   // отображение в режиме view
  onSave: (newValue: string | null) => void | Promise<void>;
  placeholder?: string;
  width?: number;
}

interface NumberProps extends BaseProps { kind: "number"; }
interface TextProps   extends BaseProps { kind: "text"; }
interface SelectProps extends BaseProps { kind: "select"; options: string[]; }
type Props = NumberProps | TextProps | SelectProps;


export function EditableCell(props: Props) {
  const { value, saving = false, format, onSave, placeholder = "—", align = "left", width } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) inputRef.current.select();
    }
  }, [editing]);

  function startEdit() {
    if (saving) return;
    setDraft(value === null || value === undefined ? "" : String(value));
    setEditing(true);
  }

  async function commit() {
    if (!editing) return;
    setEditing(false);
    const cleaned = draft.trim();
    const newVal = cleaned === "" ? null : cleaned;
    const oldVal = value === null || value === undefined ? null : String(value);
    if (newVal === oldVal) return;   // ничего не меняем
    await onSave(newVal);
  }

  function cancel() {
    setEditing(false);
    setDraft("");
  }

  const display = format
    ? format(value)
    : (value === null || value === undefined || value === "" ? placeholder : String(value));
  const isEmpty = value === null || value === undefined || value === "";

  const cellStyle: React.CSSProperties = {
    cursor: saving ? "wait" : "text",
    textAlign: align,
    color: isEmpty ? "var(--mute)" : undefined,
    padding: "2px 6px",
    minHeight: 22,
    width,
    opacity: saving ? 0.5 : 1,
    background: editing ? "var(--paper)" : undefined,
  };

  if (!editing) {
    return (
      <div
        role="button" tabIndex={0}
        onClick={startEdit}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "F2") startEdit(); }}
        style={cellStyle}
        title="Кликните чтобы редактировать"
      >
        {display}
      </div>
    );
  }

  if (props.kind === "select") {
    return (
      <select
        ref={(el) => { inputRef.current = el; }}
        className="input-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); void commit(); }
          if (e.key === "Escape") cancel();
        }}
        style={{ width: "100%", fontSize: 12, padding: "1px 4px" }}
      >
        <option value="">—</option>
        {props.options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  const isNumber = props.kind === "number";
  return (
    <input
      ref={(el) => { inputRef.current = el; }}
      type="text"
      inputMode={isNumber ? "decimal" : undefined}
      className="input-sm"
      value={draft}
      onChange={(e) => {
        const v = isNumber ? e.target.value.replace(/[^\d.,\s-]/g, "") : e.target.value;
        setDraft(v);
      }}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); void commit(); }
        if (e.key === "Escape") cancel();
      }}
      style={{
        width: "100%", fontSize: 12, padding: "1px 4px",
        textAlign: align,
      }}
    />
  );
}
